import React, { useRef } from "react";

import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { useSelector } from "react-redux";

import Badge from "components/Badge";
import IconTextButton from "components/Button/IconTextButton";
import { MetadataField, PrimaryHeading } from "components/Card/Elements";
import { Chip } from "components/Chip";
import { Row } from "components/Layout";
import { ScanLabelsPopover } from "components/ScanLabelsPopover";

import { SaleyardPermission } from "constants/permissions";
import { userTypes } from "constants/users";

import { ForScaleOperator, ForUserType } from "containers/ForUserType";

import { getNestedConsignments, getQueuedScanLabelNames } from "selectors";

import { useToggle } from "hooks";
import { useHasSaleyardPermission } from "hooks/useHasPermission";

const TypedForUserType = ForUserType as React.FC<{
  userType: string | string[];
  children?: React.ReactNode;
}>;

interface NestedConsignment {
  id: string | number;
  vendorName?: string;
  quantity?: number;
  quantityUndrafted?: number;
  scannedCount?: number;
}

interface ConsignmentDetailHeaderProps {
  consignmentId: string | number;
}

function ConsignmentDetailHeader(props: ConsignmentDetailHeaderProps) {
  const { consignmentId } = props;

  const [isScanLabelsPopoverVisible, toggleIsScanLabelsPopoverVisible] =
    useToggle(false);
  const scanLabelsButtonRef = useRef<HTMLDivElement>(null);

  const consignment: NestedConsignment =
    useSelector(getNestedConsignments).find(
      (c: NestedConsignment) => c.id === consignmentId,
    ) || {};
  const queuedScanLabelNames = useSelector(getQueuedScanLabelNames);
  const hasScanLabelPermission = useHasSaleyardPermission(
    SaleyardPermission.canAddScanLabels,
  );

  const hasQueuedScanLabels = queuedScanLabelNames.length > 0;

  if (!consignmentId) {
    return null;
  }

  const { vendorName, quantity, quantityUndrafted, scannedCount } = consignment;

  return (
    <Row justifyAround alignCenter>
      <MetadataField title="Vendor" value={vendorName} />
      <MetadataField title="Head Count">
        <PrimaryHeading>
          <ForScaleOperator>{scannedCount}</ForScaleOperator>
          <TypedForUserType
            userType={[userTypes.LIVESTOCK_AGENT, userTypes.SALEYARD_ADMIN]}
          >
            {quantity}
          </TypedForUserType>
          {!!quantityUndrafted && (
            <Badge color="warning">
              {quantityUndrafted < 0 && "+"}
              {-quantityUndrafted}
            </Badge>
          )}
        </PrimaryHeading>
      </MetadataField>
      {hasScanLabelPermission && (
        <MetadataField title="Labels to be applied">
          <Row alignCenter gridGap={1}>
            {hasQueuedScanLabels && (
              <Row alignCenter flexWrap>
                {queuedScanLabelNames.map(name => (
                  <Chip key={name}>{name}</Chip>
                ))}
              </Row>
            )}
            <div ref={scanLabelsButtonRef}>
              <IconTextButton
                icon={faPlus}
                onClick={toggleIsScanLabelsPopoverVisible}
                inline
                iconSize={18}
                textSize={14}
              >
                Add
              </IconTextButton>
            </div>
          </Row>
        </MetadataField>
      )}
      {hasScanLabelPermission && (
        <ScanLabelsPopover
          anchorEl={scanLabelsButtonRef.current}
          isOpen={isScanLabelsPopoverVisible}
          closeSelf={toggleIsScanLabelsPopoverVisible}
        />
      )}
    </Row>
  );
}

export default React.memo(ConsignmentDetailHeader);
